import {
  ProgressIndicator,
  VerticalDivider,
} from "@fluentui/react";
import icons from "components/shared/Icons";
import customTheme from "helpers/customTheme";
import useRouter from "hooks/useRouter";
import { routeDefinitions } from "router";

import usePolicyEditPanelManager from "hooks/policy/usePolicyEditPanelManager";
import { PolicyEditPanelType } from "store/policyEditPanelManagementSlice";
import { useMemo } from "react";
import useObserver from "hooks/useObserver";
import { INode } from "models/node";
import { useTrackingContext } from "components/shared/TrackingContext";
import HeaderButton from "components/shared/HeaderButton";
import { hasReadOnlyAccess } from "components/AppContent";
import { usePolicyEditStructureNodeManager } from "./PolicyEditStructureNodeManager";

// Local interface to match the node manager
interface IPolicyTemplateSlot {
  id: string;
  name: string;
  templateId: string;
  position: number;
}





function PolicyEditStructureHeader() {
  // Props will be used when implementing actual functionality
  const isLoading = false; // TODO: Get from policy edit state
  const {
    nodeTree,
    nodeSelection,
    getSelectedNodes,
    deleteSelectedTemplates,
    isDeleted,
    policyInfo,
  } = usePolicyEditStructureNodeManager();
  const { openPanel } = usePolicyEditPanelManager();
  const { goToRoute } = useRouter();
  useObserver(nodeSelection.observable);
  const { trackEvent } = useTrackingContext();

  const selectedNodes: INode<IPolicyTemplateSlot>[] = useMemo(() => {
    const nodes = getSelectedNodes();
    return nodes;
  }, [getSelectedNodes]);

  const hasSelection = selectedNodes.length > 0;
  const hasTemplates = Object.keys(nodeTree.nodes).length > 0;

  // Check if any selected node has criteria defined
  const hasCriteria = useMemo(() => selectedNodes.some(node => {
    const constraints = (node.content as any)?.constraints;
    return constraints && Array.isArray(constraints) && constraints.length > 0;
  }), [selectedNodes]);

  // Check if policy is in Draft status
  const isPolicyDraft = policyInfo?.status === 'Draft';

  return (
    <>
      <div
        style={{
          display: "flex",
          height: 45,
          background: customTheme.actionsBackgroundColor,
        }}
      >
        <HeaderButton
          data-automation-id="policyEdit-structure-addTemplate-button"
          text="Add template"
          iconProps={icons.add}
          disabled={isLoading || hasReadOnlyAccess() || (hasTemplates && !isDeleted)}
          onClick={() => {
            openPanel(PolicyEditPanelType.AddTemplate);
            trackEvent("Add template button clicked");
          }}
        />
        <VerticalDivider />
        <HeaderButton
          data-automation-id="policyEdit-structure-editCriteria-button"
          text="Edit criteria"
          iconProps={icons.edit}
          disabled={!hasSelection || isLoading || hasReadOnlyAccess() || !hasCriteria || isDeleted || !isPolicyDraft}
          onClick={() => {
            openPanel(PolicyEditPanelType.EditCriteria);
            trackEvent("Edit criteria button clicked");
          }}
        />

        <VerticalDivider />
        <HeaderButton
          data-automation-id="policyEdit-structure-openTemplate-button"
          text="Open template"
          iconProps={icons.openFile}
          disabled={!hasSelection || isLoading || isDeleted}
          onClick={() => {
            if (hasSelection && selectedNodes.length > 0) {
              const selectedTemplate = selectedNodes[0]; // Get the first selected template
              const templateId = selectedTemplate.content?.templateId;
              if (templateId) {
                goToRoute(
                  routeDefinitions.TemplateEdit.getRouteInfo({
                    templateId,
                  })
                );
              }
            }
            trackEvent("Open template button clicked");
          }}
        />
        <VerticalDivider />
        <HeaderButton
          data-automation-id="policyEdit-structure-delete-button"
          text="Delete"
          iconProps={icons.delete}
          disabled={!hasSelection || isLoading || hasReadOnlyAccess() || isDeleted || !isPolicyDraft}
          onClick={() => {
            if (deleteSelectedTemplates) {
              deleteSelectedTemplates();
              trackEvent("Delete template button clicked");
            }
          }}
        />
      </div>
      <ProgressIndicator
        ariaValueText="Progress bar"
        styles={{
          itemProgress: {
            padding: 0,
            visibility: isLoading ? undefined : "hidden",
          },
        }}
      />
      {Object.keys(nodeTree.nodes).length > 0 && !isDeleted && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.5fr",
            borderBottom: `1px solid ${customTheme.divBorderColor}`,
            fontWeight: 600
          }}
        >
          <div style={{ padding: "12px" }}>Template name</div>
          <div style={{ padding: "12px" }}>Include policy if</div>
        </div>
      )}

    </>
  );
}

export default PolicyEditStructureHeader;